import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import { Authorization } from "../../../Context/Authorization";
import { AuthContext } from "../../../Context/AuthContext";
import Preloader from "../../../shared/PreLoaders";
import { logActivity } from "../../../utils/LogActivity";

const inputStyle = {
    width: "100%",
    padding: "9px 12px",
    border: "1.5px solid #E2E8F0",
    borderRadius: 8,
    fontSize: 13,
    color: "#1A202C",
    background: "#fff",
    outline: "none",
    transition: "border-color 0.2s",
};

const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: "#2D3748",
    marginBottom: 6,
    display: "block",
};

const fieldWrap = {
    marginBottom: 16,
};

const focusStyle = (e) => {
    e.target.style.borderColor = "#2E7D32";
};

const blurStyle = (e) => {
    e.target.style.borderColor = "#E2E8F0";
};

const CreateEcoTourismFee = () => {
    const Api = process.env.REACT_APP_API;

    const { userId } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);

    const [ecoTourisms, setEcoTourisms] = useState([]);

    const [formData, setFormData] = useState({
        ecoTourismId: "",
        feeType: "",
        feeMode: "fixed",
        amount: "",
        description: "",
    });

    // ─────────────────────────────
    // FETCH ECO TOURISM
    // ─────────────────────────────
    useEffect(() => {
        fetchEcoTourisms();
    }, []);

    const fetchEcoTourisms = async () => {
        try {
            setLoading(true);

            const config = Authorization();

            const res = await axios.get(
                `${Api}/api/eco-tourism`,
                config
            );

            setEcoTourisms(res.data.data || []);
        } catch (err) {
            console.error(err);

            toast.error(
                "Failed to load Eco Tourism list"
            );
        } finally {
            setLoading(false);
        }
    };

    // ─────────────────────────────
    // HANDLE CHANGE
    // ─────────────────────────────
    const handleChange = (e) => {
        const { id, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [id]: value,
        }));
    };

    // ─────────────────────────────
    // SUBMIT
    // ─────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setLoading(true);

            const config = Authorization();

            const payload = {
                ecoTourismId: formData.ecoTourismId,
                feeType: formData.feeType,
                amount:
                    formData.feeMode === "fixed"
                        ? Number(formData.amount)
                        : null,

                feeMode: formData.feeMode,
                description: formData.description,
            };

            const response = await axios.post(
                `${Api}/api/eco-tourism-fees`,
                payload,
                config
            );

            if (response.data.success) {
                toast.success(
                    "Eco Tourism Fee Created Successfully"
                );

                await logActivity(
                    userId,
                    "CREATE",
                    "Created Eco Tourism Fee"
                );

                // RESET
                setFormData({
                    ecoTourismId: "",
                    feeType: "",
                    amount: "",
                });
            }
        } catch (error) {
            console.error(error);

            toast.error(
                error.response?.data?.error ||
                "Failed to create fee"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            {loading && (
                <Preloader loading={loading} />
            )}

            {/* ── Header ── */}
            <div style={{ marginBottom: 20 }}>
                <h4
                    style={{
                        margin: 0,
                        fontWeight: 700,
                        color: "#1A202C",
                        fontSize: 18,
                    }}
                >
                    Eco Tourism Fee
                </h4>

                <div
                    style={{
                        fontSize: 12,
                        color: "#718096",
                        marginTop: 3,
                    }}
                >
                    <span style={{ color: "#A0AEC0" }}>
                        Eco Tourism
                    </span>

                    <span
                        style={{
                            margin: "0 6px",
                            color: "#CBD5E0",
                        }}
                    >
                        ›
                    </span>

                    <span
                        style={{
                            color: "#2E7D32",
                            fontWeight: 600,
                        }}
                    >
                        Create Fee Type
                    </span>
                </div>
            </div>

            {/* ── Form Card ── */}
            <div
                style={{
                    background: "#fff",
                    borderRadius: 12,
                    border: "1px solid #E2E8F0",
                    boxShadow:
                        "0 1px 4px rgba(0,0,0,0.06)",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "14px 20px",
                        background:
                            "linear-gradient(135deg,#1B5E20,#2E7D32)",
                    }}
                >
                    <span
                        style={{
                            width: 30,
                            height: 30,
                            background:
                                "rgba(255,255,255,0.15)",
                            borderRadius: 8,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: 15,
                        }}
                    >
                        <i className="mdi mdi-cash" />
                    </span>

                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#fff",
                        }}
                    >
                        Eco Tourism Fee Form
                    </span>
                </div>

                {/* Body */}
                <div
                    style={{
                        padding: "24px 28px",
                    }}
                >
                    <form onSubmit={handleSubmit}>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns:
                                    "1fr 1fr",
                                gap: 20,
                            }}
                        >
                            {/* Eco Tourism */}
                            <div style={fieldWrap}>
                                <label
                                    htmlFor="ecoTourismId"
                                    style={labelStyle}
                                >
                                    Eco Tourism
                                </label>

                                <select
                                    id="ecoTourismId"
                                    value={
                                        formData.ecoTourismId
                                    }
                                    onChange={handleChange}
                                    required
                                    style={{
                                        ...inputStyle,
                                        cursor: "pointer",
                                    }}
                                    onFocus={focusStyle}
                                    onBlur={blurStyle}
                                >
                                    <option value="">
                                        Select Eco Tourism
                                    </option>

                                    {ecoTourisms.map((eco) => (
                                        <option
                                            key={eco._id}
                                            value={eco._id}
                                        >
                                            {eco.ecoTourismName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Fee Type */}
                            <div style={fieldWrap}>
                                <label
                                    htmlFor="feeType"
                                    style={labelStyle}
                                >
                                    Fee Type
                                </label>

                                <input
                                    id="feeType"
                                    type="text"
                                    value={formData.feeType}
                                    onChange={handleChange}
                                    required
                                    placeholder="Adult Entry / Child Entry / Parking"
                                    style={inputStyle}
                                    onFocus={focusStyle}
                                    onBlur={blurStyle}
                                />
                            </div>
                            <div style={fieldWrap}>
                                <label htmlFor="feeMode" style={labelStyle}>
                                    Fee Mode
                                </label>

                                <select
                                    id="feeMode"
                                    value={formData.feeMode}
                                    onChange={handleChange}
                                    style={{
                                        ...inputStyle,
                                        cursor: "pointer",
                                    }}
                                    onFocus={focusStyle}
                                    onBlur={blurStyle}
                                >
                                    <option value="fixed">Fixed Amount</option>
                                    <option value="variable">Variable Amount</option>
                                </select>
                            </div>
                            {/* Amount */}
                            {formData.feeMode === "fixed" && (
                                <div style={fieldWrap}>
                                    <label htmlFor="amount" style={labelStyle}>
                                        Amount
                                    </label>

                                    <input
                                        id="amount"
                                        type="number"
                                        min="0"
                                        value={formData.amount}
                                        onChange={handleChange}
                                        required
                                        placeholder="Enter amount"
                                        style={inputStyle}
                                        onFocus={focusStyle}
                                        onBlur={blurStyle}
                                    />
                                </div>
                            )}

                            <div style={fieldWrap}>
                                <label htmlFor="description" style={labelStyle}>
                                    Description
                                </label>

                                <input
                                    id="description"
                                    type="text"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Optional notes"
                                    style={inputStyle}
                                    onFocus={focusStyle}
                                    onBlur={blurStyle}
                                />
                            </div>
                        </div>

                        {/* Buttons */}
                        <div
                            style={{
                                display: "flex",
                                gap: 12,
                                marginTop: 8,
                            }}
                        >
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 7,
                                    padding: "10px 24px",
                                    background:
                                        "linear-gradient(135deg,#1B5E20,#2E7D32)",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 9,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    cursor: loading
                                        ? "not-allowed"
                                        : "pointer",
                                    boxShadow:
                                        "0 3px 10px rgba(46,125,50,0.3)",
                                    opacity: loading ? 0.7 : 1,
                                }}
                            >
                                <i className="mdi mdi-check" />

                                {loading
                                    ? "Saving..."
                                    : "Submit"}
                            </button>

                            <button
                                type="reset"
                                onClick={() =>
                                    setFormData({
                                        ecoTourismId: "",
                                        feeType: "",
                                        amount: "",
                                    })
                                }
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 7,
                                    padding: "10px 20px",
                                    background: "#F7FAFC",
                                    color: "#4A5568",
                                    border:
                                        "1px solid #E2E8F0",
                                    borderRadius: 9,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateEcoTourismFee;